/**
 * Price comparison helpers.
 *
 * There is deliberately NO synthetic "was" price generator here. A former
 * strikethrough price was previously invented from a hash of the product id
 * whenever Shopify had no real `compareAtPrice`. That was removed by owner
 * decision: a strikethrough may only ever be rendered from Shopify's real
 * `compareAtPrice`, and the sale predicate must use that same real value.
 * Do not reintroduce a generated original price.
 */

/**
 * Returns the discount percentage between original and current price.
 */
export function getDiscountPercent(originalPrice: number, currentPrice: number): number {
  if (originalPrice <= 0 || currentPrice <= 0) return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

/**
 * The single source of truth for "is this product really discounted?".
 * Real Shopify compare-at price only.
 */
export function realOriginalPrice(
  currentPrice: number,
  compareAtPrice?: number | string | null,
): number | null {
  const compareAt = typeof compareAtPrice === "string" ? parseFloat(compareAtPrice) : compareAtPrice ?? 0;
  if (!compareAt || !currentPrice || currentPrice <= 0) return null;
  return compareAt > currentPrice ? compareAt : null;
}

/**
 * Brand rule: a sale never runs deeper than 30%. The supplier sets compare-at
 * prices on a few sold-out rugs ("56% OFF"); a cut that deep reads as invented.
 */
export const MAX_SALE_DISCOUNT_PCT = 30;

/**
 * The "was" price a page may show: a real compare-at price, on something that
 * can be bought, within the brand's sale cap. Otherwise null (no strikethrough).
 */
export function genuineOriginalPrice(
  currentPrice: number,
  compareAtPrice?: number | string | null,
  available = true,
): number | null {
  if (!available) return null;
  const original = realOriginalPrice(currentPrice, compareAtPrice);
  if (!original) return null;
  return getDiscountPercent(original, currentPrice) <= MAX_SALE_DISCOUNT_PCT ? original : null;
}
